import { json, type LoaderFunctionArgs } from '@remix-run/node';
import type { ShouldRevalidateFunction } from '@remix-run/react';
import { z } from 'zod';
import { getConversations } from '~/modules/chat/api/get-conversations.server';
import { UPDATE_CONVERSATION_API } from './remix-api.chat.update-conversation';
import { UPDATE_CONVERSATION_THUMBNAIL_API } from './remix-api.chat.update-thumbnail';
import { ADD_PARTICIPANTS_API } from './remix-api.chat.add-participants';
import { REMOVE_PARTICIPANT_API } from './remix-api.chat.remove-participant';
import { MUTE_CONVERSATION_API } from './remix-api.chat.mute';
import { MARK_CONVERSATION_READ_API } from './remix-api.chat.mark-conversation-read';
import { MARK_CONVERSATION_UNREAD_API } from './remix-api.chat.mark-conversation-unread';
import { REACT_MESSAGE_API } from './remix-api.chat.react-message';
import { DELETE_MESSAGE_API } from './remix-api.chat.delete-message';
import { UPDATE_CONVERSATION_SETTINGS_API } from './remix-api.chat.update-settings';

export const CONVERSATIONS_API = '/remix-api/chat/conversations';

const QUERY_SCHEMA = z.object({
    professionalID: z.coerce.number().int().nonnegative().nullish(),
    q: z.string().nullish(),
    buildHubIDs: z.array(z.string().min(1)).nullish(),
    isArchived: z.coerce
        .number()
        .int()
        .transform((val) => !!val)
        .nullish(),
    limit: z.coerce.number().int().positive().max(100).nullish(),
    offset: z.coerce.number().int().nonnegative().nullish(),
});

/**
 * API endpoint to get the user's conversations. Use with the
 * `useConversations` hook.
 * @see {@link packages/web/app/modules/chat/hooks/use-conversations.ts}
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);
    const result = QUERY_SCHEMA.safeParse({
        professionalID: url.searchParams.get('professionalID'),
        q: url.searchParams.get('q'),
        buildHubIDs: url.searchParams.getAll('buildHubIDs'),
        isArchived: url.searchParams.get('isArchived'),
        limit: url.searchParams.get('limit'),
        offset: url.searchParams.get('offset'),
    });

    if (!result.success) {
        return json(null, { status: 400 });
    }

    try {
        const conversations = await getConversations({
            context,
            professionalID: result.data.professionalID ?? undefined,
            search: result.data.q ?? undefined,
            buildHubIDs: result.data.buildHubIDs ?? undefined,
            isArchived: result.data.isArchived ?? undefined,
            limit: result.data.limit ?? undefined,
            offset: result.data.offset ?? undefined,
        });

        if (!conversations) {
            return json(null, { status: 500 });
        }

        return json({
            conversations: conversations.nodes,
            pageInfo: conversations.pageInfo,
        });
    } catch (error) {
        logger.error(`Error in ${CONVERSATIONS_API}`, { error });
        return json(null, { status: 500 });
    }
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
    defaultShouldRevalidate,
    formAction,
}) => {
    if (
        formAction === UPDATE_CONVERSATION_API ||
        formAction === UPDATE_CONVERSATION_THUMBNAIL_API ||
        formAction === ADD_PARTICIPANTS_API ||
        formAction === REMOVE_PARTICIPANT_API ||
        formAction === MUTE_CONVERSATION_API ||
        formAction === MARK_CONVERSATION_READ_API ||
        formAction === MARK_CONVERSATION_UNREAD_API ||
        formAction === REACT_MESSAGE_API ||
        formAction === DELETE_MESSAGE_API ||
        formAction === UPDATE_CONVERSATION_SETTINGS_API
    ) {
        // Let the conversation cache update the conversation instead.
        return false;
    }
    return defaultShouldRevalidate;
};
